import React, { useRef, useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import { Swiper as SwiperCore } from 'swiper/types'
import {
  PrevChevron,
  NextChevron
} from '@toasttab/shared/src/components/molecules/SliderNavigation/NavigationIcons'

import { Badge } from '@toasttab/shared/src/components'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

const BusinessTypes = ({ body }: { body: any }) => {
  const { t } = useI18next()
  const [prevActive, setPreviousActive] = useState(false)
  const [nextActive, setNextActive] = useState(true)
  const [width, setWidth] = useState<number>(0)
  const [businessType, setBusinessType] = useState<
    'Restaurants' | 'Retailers' | null
  >(null)
  const [typeCategory, setTypeCategory] = useState<string | null>(null)
  const [categoryLink, setCategoryLink] = useState<string | null>(null)

  function handleWindowSizeChange() {
    setWidth(window.innerWidth)
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  const businessTypeStyles = (selected: boolean) =>
    classNames('!w-1/2 !h-11 !px-6 !py-2.5', {
      '!bg-black/100 !text-white': selected,
      '!bg-transparent !text-default': !selected
    })

  const categorySectionStyles = (selected: boolean) =>
    classNames({
      block: selected,
      hidden: !selected
    })
  const typeCategoryStyles = (selected: boolean) =>
    classNames('mb-6 last:mb-0 px-8 hidden lg:block', {
      'border-l-2 border-brand-50': selected,
      'border-none': !selected
    })

  const categoryLinkStyles = (selected: boolean) =>
    classNames('cursor-pointer mt-1', {
      block: selected,
      hidden: !selected
    })

  const switchCategory = (categoryOption: string) => {
    for (const category of body.tabs) {
      const selectedCategory = category.categroies.find(
        (category: any) => category.title === categoryOption
      )
      if (selectedCategory) {
        setTypeCategory(selectedCategory.title)
        setCategoryLink(selectedCategory.link)
        break
      }
    }
  }

  const setDefaultBusinessType = () => {
    setBusinessType(body.tabs[0].title as 'Restaurants' | 'Retailers')
  }

  const setDefaultCategory = () => {
    const defaultCategory = body.tabs.find(
      (item: any) => item.title === businessType
    )
    if (defaultCategory !== null) {
      const firstCategory = defaultCategory?.categroies[0]?.title
      setTypeCategory(firstCategory as string)
    }
  }

  const updateBusinessType = (type: 'Restaurants' | 'Retailers') => {
    setBusinessType(type)

    const defaultCategory = body.tabs.find((item: any) => item.title === type)

    if (defaultCategory !== null) {
      const firstCategory = defaultCategory?.categroies?.[0]?.title
      setTypeCategory(firstCategory as string)
    }
  }

  const renderAsset = (src: string) => {
    const assetType = src.split('.').pop()

    if (assetType === 'mp4') {
      return (
        <video
          autoPlay
          loop
          muted
          className='w-auto'
          playsInline
          preload='metadata'
        >
          <source src={src} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      )
    } else {
      return <img src={src} alt='asset' className='w-auto' />
    }
  }

  const renderDesktopAssets = (assets: any) => {
    return (
      <>
        <div className='flex flex-col max-w-[266px] mr-6 items-between justify-between'>
          {renderAsset(assets[0]?.src)}
          {renderAsset(assets[1]?.src)}
        </div>
        {renderAsset(assets[2]?.src)}
      </>
    )
  }

  useEffect(() => {
    if (businessType === null) {
      setDefaultBusinessType()
    }

    const selectedCategory = body.tabs.find(
      (item: any) => item.title === businessType
    )

    const hasCategory = selectedCategory?.categroies.find(
      (category: any) => category.title === typeCategory
    )

    if (!hasCategory) {
      setDefaultCategory()
    }
    switchCategory(typeCategory)
  }, [businessType, typeCategory])

  const swiperRef = useRef<SwiperCore>(null)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!swiperRef.current) return

      if (e.key === 'ArrowLeft') {
        swiperRef.current.slidePrev()
      } else if (e.key === 'ArrowRight') {
        swiperRef.current.slideNext()
      }
    },
    []
  )

  const iconSize = width < 768 ? 'large' : 'small'

  const prevHeapInteraction = 'hplogos_backarrow_click'
  const nextHeapInteraction = 'hplogos_forwardarrow_click'

  return (
    <section className='mb-10 lg:mb-16 overflow-hidden'>
      <div className='ds-wrapper'>
        <div className='ds-container lg:ds-grid'>
          <div className='col-span-4 justify-center content-center'>
            <div className='bg-gradient-to-t from-[#F6F1EE] via-[#F6F1EE] to-[#F9F9F9] rounded-[2rem] lg:min-h-[564px]'>
              <div className='bg-[#F6F1EE] bg-blend-multiply bg-[#F6F1EE80]/50 p-2.5 rounded-[2rem]'>
                {body.tabs.map((type: any, index: any) => (
                  <Button
                    key={index}
                    heapInteraction={type.heapInteraction}
                    variation='pill'
                    size='large'
                    onClick={() =>
                      updateBusinessType(
                        type.title as 'Restaurants' | 'Retailers'
                      )
                    }
                    classes={businessTypeStyles(businessType === type.title)}
                  >
                    {type.title}
                    {type.badge && (
                      <Badge
                        text={type.badge}
                        badgeColor='bg-yellow-25'
                        classes='ml-2.5'
                        uppercase={true}
                      />
                    )}
                  </Button>
                ))}
              </div>
              <div className='py-6'>
                {body.tabs.map((type: any, index: any) => (
                  <div
                    key={index}
                    className={categorySectionStyles(
                      businessType === type.title
                    )}
                  >
                    <div className='px-4 lg:hidden'>
                      <select
                        className='block lg:hidden text-secondary px-4 py-3 rounded-[100px] bg-white bg-arrow-drop-down-dark bg-no-repeat bg-[center_right_1rem] appearance-none mb-4 w-full'
                        onChange={(e) => switchCategory(e.target.value)}
                      >
                        {type.categroies.map((category: any, index: any) => (
                          <option
                            key={index}
                            value={category.title}
                            data-link={category.link}
                          >
                            {category.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    {type.categroies.map((category: any, index: any) => (
                      <React.Fragment
                        key={`${type.title}-${category.title}-${index}`}
                      >
                        <div
                          className={`flex relative mx-auto lg:hidden pl-4 ${
                            typeCategory === (category.title as string)
                              ? 'flex lg:hidden'
                              : 'hidden'
                          }`}
                          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                            handleKeyDown(e)
                          }
                          tabIndex={0}
                        >
                          <Swiper
                            modules={[Navigation]}
                            speed={300}
                            threshold={30}
                            grabCursor
                            spaceBetween={24}
                            a11y={{ enabled: true }}
                            loop={false}
                            slidesPerView='auto'
                            onBeforeInit={(swiper) => {
                              swiperRef.current = swiper
                            }}
                            onSlideChange={(swiper) => {
                              if (swiper.isEnd) {
                                setNextActive(false)
                              } else {
                                setNextActive(true)
                              }

                              if (swiper.isBeginning) {
                                setPreviousActive(false)
                              } else {
                                setPreviousActive(true)
                              }
                            }}
                            navigation={{
                              nextEl: '.swiper-next',
                              prevEl: '.swiper-prev'
                            }}
                            className='swiper-height--full'
                          >
                            {category.assets.map((asset: any, i: number) => (
                              <SwiperSlide
                                key={i}
                                className='!max-w-[252px] !h-[241px] !border-box mb-4'
                              >
                                {renderAsset(asset.src)}
                              </SwiperSlide>
                            ))}
                            <div className='flex mx-auto mb-4 lg:hidden'>
                              <PrevChevron
                                active={prevActive}
                                size={iconSize}
                                heapInteraction={prevHeapInteraction}
                                classes=' mr-4'
                              />
                              <NextChevron
                                active={nextActive}
                                size={iconSize}
                                heapInteraction={nextHeapInteraction}
                              />
                            </div>
                          </Swiper>
                        </div>
                        <div
                          key={index}
                          className={typeCategoryStyles(
                            typeCategory === (category.title as string)
                          )}
                        >
                          <Button
                            heapInteraction={category.heapInteraction}
                            key={index}
                            variation='text'
                            size='large'
                            onClick={() => switchCategory(category.title)}
                            classes='!h-auto !p-0 font-effra font-normal lg:!leading-[2rem] lg:!text-[1.625rem] !text-secondary'
                          >
                            {category.title}
                          </Button>
                          <TextLink
                            href={category.link}
                            icon='arrow'
                            isDisplayedBlock
                            text={`${t('Learn More')}`}
                            wrapperClasses={categoryLinkStyles(
                              typeCategory === (category.title as string)
                            )}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                ))}
                <div className='px-4'>
                  <Button
                    href={categoryLink ? categoryLink : '/'}
                    variation='primary'
                    size='large'
                    classes='block lg:hidden'
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className='col-span-12 lg:col-span-8 hidden lg:block'>
            {body.tabs.map((type: any, typeIndex: any) => (
              <React.Fragment key={`desktop-${type.title}-${typeIndex}`}>
                {type.categroies.map((category: any, index: any) => (
                  <div
                    key={index}
                    className={
                      typeCategory === (category.title as string)
                        ? 'block flex'
                        : 'hidden'
                    }
                  >
                    {renderDesktopAssets(category.assets)}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default BusinessTypes
